import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import MetaTags from "game-app/MetaTags";
import LoginScreenWrapper from "game-app/login";

import ResetPasswordConfirmScreen from "auth/ResetPasswordConfirmScreen";
import ResetPasswordScreen from "auth/ResetPasswordScreen";

import { GameAuthProvider, useAuthContext } from "context/GameAuthContext";
import { GameProvider } from "context/GameContext";
import { OrganizationProvider } from "context/OrganisationContext";
import { RoomTemplatePageContentProvider } from "context/RoomTemplateContext";
import { SocketProvider } from "context/SocketContext";

import UserDashboardScreen from "user-dashboard/page/UserDashboardScreen";
import BuddyDutyInvitation from "user-dashboard/page/BuddyDutyInvitation";
import UserQuestionnaire from "user-questionnaire/UserQuestionnaire";

import { isFeatureEnabled } from "feature-configs/featureConfigUtils";
import { FeatureConfig } from "feature-configs/featureConfigApi";

import { ThemeWrapper } from "theme/theme-wrapper";

import RoomStatusRendering from "company/RoomManagement";

const EmployeeOnboardingRoutes = lazy(() => import("pages/employee-onboarding/EmployeeOnboardingRoutes"));

const socketUrl = process.env.REACT_APP_SOCKET_URL || window.location.origin;

const BuddyRoutes = () => {
	return (
		<Routes>
			<Route path="invitation" element={<BuddyDutyInvitation />} />
			<Route path="user-questionnaire" element={<UserQuestionnaire />} />
		</Routes>
	);
};

const InitializeGameApp = () => {
	return (
		<GameAuthProvider>
			<AppWrapper />
		</GameAuthProvider>
	);
};

const AppWrapper = () => {
	const { user } = useAuthContext();

	return (
		<Routes>
			{isFeatureEnabled(FeatureConfig.buddy) && (
				<Route
					path="buddy/*"
					element={
						<ProtectedRoute user={user}>
							<OrganizationProvider>
								<ThemeWrapper>
									<BuddyRoutes />
								</ThemeWrapper>
							</OrganizationProvider>
						</ProtectedRoute>
					}
				/>
			)}

			<Route
				path="onboarding/*"
				element={
					<ProtectedRoute user={user}>
						<OrganizationProvider>
							<ThemeWrapper htmlFontSize={10}>
								<EmployeeOnboardingRoutes />
							</ThemeWrapper>
						</OrganizationProvider>
					</ProtectedRoute>
				}
			/>
			<Route
				path="user-dashboard/*"
				element={
					<ProtectedRoute user={user}>
						<OrganizationProvider>
							<ThemeWrapper>
								<UserDashboardScreen />
							</ThemeWrapper>
						</OrganizationProvider>
					</ProtectedRoute>
				}
			/>
			<Route path=":slug?/*" element={<App />} />
		</Routes>
	);
};

const App = () => {
	const { user } = useAuthContext();

	return (
		<OrganizationProvider>
			<RoomTemplatePageContentProvider>
				<MetaTags />
				<ThemeWrapper>
					<Routes>
						<Route path="" element={user ? <AuthenticatedAppGameRoom /> : <LoginScreenWrapper />} />
						<Route path=":story" element={user ? <AuthenticatedAppGameRoom /> : <LoginScreenWrapper />} />
						<Route path="reset-password" element={<ResetPasswordScreen />} />
						<Route path="reset-password/confirm" element={<ResetPasswordConfirmScreen />} />
					</Routes>
				</ThemeWrapper>
			</RoomTemplatePageContentProvider>
		</OrganizationProvider>
	);
};

const AuthenticatedAppGameRoom = () => {
	return (
		<SocketProvider socketUrl={socketUrl!}>
			<GameProvider>
				<RoomStatusRendering />
			</GameProvider>
		</SocketProvider>
	);
};

function ProtectedRoute({ user, children }) {
	if (user) {
		return children;
	}
	return <Navigate to="/" />;
}

export default InitializeGameApp;
